/**
 * @module stores/zoteroStore
 * @description Zotero state management for Preparo bibliography integration
 *
 * Manages:
 * - User and group libraries
 * - Collections (with tree structure)
 * - Items within collections
 * - Selection state
 */

import { writable, derived, get } from 'svelte/store';
import { createLogger } from '@services/loggerService';
import {
  zoteroService,
  type StoredLibrary,
  type ZoteroGroup,
  type ZoteroCollection,
  type ZoteroItem,
  type LibraryType,
} from '@services/zoteroService';

const log = createLogger('zoteroStore');

// =============================================================================
// TYPES
// =============================================================================

export interface ZoteroState {
  /** Zotero user ID */
  userId: number | null;
  /** Username */
  username: string | null;
  /** User's personal library */
  userLibrary: StoredLibrary | null;
  /** Group libraries */
  groups: ZoteroGroup[];
  /** Currently selected library */
  selectedLibrary: StoredLibrary | null;
  /** Collections for current library */
  collections: ZoteroCollection[];
  /** Collection tree (built from flat list) */
  collectionTree: ZoteroCollection[];
  /** Expanded collection keys */
  expandedKeys: Set<string>;
  /** Selected collection key */
  selectedCollectionKey: string | null;
  /** Items in selected collection */
  items: ZoteroItem[];
  /** Total items count */
  totalItems: number;
  /** Loading states */
  isLoading: boolean;
  isLoadingCollections: boolean;
  isLoadingItems: boolean;
  /** Error message */
  error: string | null;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: ZoteroState = {
  userId: null,
  username: null,
  userLibrary: null,
  groups: [],
  selectedLibrary: null,
  collections: [],
  collectionTree: [],
  expandedKeys: new Set(),
  selectedCollectionKey: null,
  items: [],
  totalItems: 0,
  isLoading: false,
  isLoadingCollections: false,
  isLoadingItems: false,
  error: null,
};

// =============================================================================
// STORE
// =============================================================================

const store = writable<ZoteroState>(initialState);

/** Exported read-only store */
export const zoteroStore = {
  subscribe: store.subscribe,
};

// =============================================================================
// DERIVED STORES
// =============================================================================

/** All available libraries (user + groups) */
export const allLibraries = derived(store, ($store) => {
  const libraries: StoredLibrary[] = [];

  if ($store.userLibrary) {
    libraries.push($store.userLibrary);
  }

  for (const group of $store.groups) {
    libraries.push({
      id: group.id,
      type: 'group',
      name: group.data.name,
    });
  }

  return libraries;
});

/** Currently selected library */
export const selectedLibrary = derived(store, ($store) => $store.selectedLibrary);

/** Collection tree for display */
export const collectionTree = derived(store, ($store) => $store.collectionTree);

/** Flattened collections respecting expansion state */
export const flattenedCollections = derived(store, ($store) => {
  return zoteroService.flattenCollectionTree($store.collectionTree, $store.expandedKeys);
});

/** Expanded keys set */
export const expandedKeys = derived(store, ($store) => $store.expandedKeys);

/** Selected collection key */
export const selectedCollectionKey = derived(store, ($store) => $store.selectedCollectionKey);

/** Items in selected collection */
export const items = derived(store, ($store) => $store.items);

/** Total items count */
export const totalItems = derived(store, ($store) => $store.totalItems);

/** Whether Zotero is initialized */
export const isInitialized = derived(store, ($store) => $store.userId !== null);

/** Loading state */
export const isLoading = derived(store, ($store) => $store.isLoading);

/** Collections loading state */
export const isLoadingCollections = derived(store, ($store) => $store.isLoadingCollections);

/** Items loading state */
export const isLoadingItems = derived(store, ($store) => $store.isLoadingItems);

/** Error message */
export const zoteroError = derived(store, ($store) => $store.error);

/** Option type for combined library/collection selector */
export interface LibraryCollectionOption {
  type: 'library' | 'collection';
  value: string;
  label: string;
  level: number;
  libraryId: number;
  libraryType: LibraryType;
  collectionKey?: string;
}

/** Combined library + collections options for unified selector */
export const libraryCollectionOptions = derived(
  [allLibraries, store],
  ([$allLibraries, $store]) => {
    const options: LibraryCollectionOption[] = [];

    for (const library of $allLibraries) {
      // Add library option
      options.push({
        type: 'library',
        value: `library:${library.type}:${library.id}`,
        label: library.type === 'user' ? 'My Library' : library.name,
        level: 0,
        libraryId: library.id,
        libraryType: library.type,
      });

      // If this is the selected library, add its collections
      if (
        $store.selectedLibrary?.id === library.id &&
        $store.selectedLibrary?.type === library.type
      ) {
        const flatCollections = zoteroService.flattenCollectionTree(
          $store.collectionTree,
          $store.expandedKeys,
          true // Always show all for selector
        );

        for (const collection of flatCollections) {
          options.push({
            type: 'collection',
            value: `collection:${library.type}:${library.id}:${collection.key}`,
            label: collection.data.name,
            level: (collection.level || 0) + 1,
            libraryId: library.id,
            libraryType: library.type,
            collectionKey: collection.key,
          });
        }
      }
    }

    return options;
  }
);

// =============================================================================
// ACTIONS
// =============================================================================

/** Request cancellation controller */
let collectionsAbortController: AbortController | null = null;
let itemsAbortController: AbortController | null = null;

/**
 * Initialize Zotero store (validate API key and load groups)
 */
export async function initZotero(): Promise<boolean> {
  log.info('Initializing Zotero store');
  store.update((s) => ({ ...s, isLoading: true, error: null }));

  try {
    // Validate API key and get user info
    const keyResult = await zoteroService.validateApiKey();
    if (!keyResult.success) {
      throw new Error(keyResult.error);
    }

    const { userID, username } = keyResult.data;

    // Create user library reference
    const userLibrary: StoredLibrary = {
      id: userID,
      type: 'user',
      name: 'My Library',
    };

    // Load groups
    const groupsResult = await zoteroService.getGroups(userID);
    const groups = groupsResult.success ? groupsResult.data : [];

    store.update((s) => ({
      ...s,
      userId: userID,
      username,
      userLibrary,
      groups,
      selectedLibrary: userLibrary, // Default to user library
      isLoading: false,
    }));

    // Load collections for default library
    await loadCollections(userLibrary.id, userLibrary.type);

    // Note: Items are loaded lazily when the user opens the Zotero tab

    log.info('Zotero initialized', { userId: userID, groupCount: groups.length });
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to initialize Zotero';
    log.error('Zotero initialization failed', { error: message });
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: message,
    }));
    return false;
  }
}

/**
 * Select a library
 */
export async function selectLibrary(library: StoredLibrary): Promise<void> {
  const current = get(store);
  if (current.selectedLibrary?.id === library.id && current.selectedLibrary?.type === library.type) {
    return;
  }

  log.info('Selecting library', { id: library.id, type: library.type });

  store.update((s) => ({
    ...s,
    selectedLibrary: library,
    selectedCollectionKey: null,
    collections: [],
    collectionTree: [],
    items: [],
    totalItems: 0,
  }));

  await loadCollections(library.id, library.type);
}

/**
 * Load collections for a library
 */
export async function loadCollections(
  libraryId: number,
  libraryType: LibraryType
): Promise<void> {
  // Cancel any pending request
  if (collectionsAbortController) {
    collectionsAbortController.abort();
  }
  collectionsAbortController = new AbortController();

  log.debug('Loading collections', { libraryId, libraryType });
  store.update((s) => ({ ...s, isLoadingCollections: true, error: null }));

  try {
    const result = await zoteroService.getCollections(libraryId, libraryType, {
      signal: collectionsAbortController.signal,
    });

    if (!result.success) {
      if (result.error === 'Request aborted') return;
      throw new Error(result.error);
    }

    const collections = result.data;
    const tree = zoteroService.buildCollectionTree(collections);

    store.update((s) => ({
      ...s,
      collections,
      collectionTree: tree,
      isLoadingCollections: false,
    }));

    log.info('Collections loaded', { count: collections.length });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return;

    const message = error instanceof Error ? error.message : 'Failed to load collections';
    log.error('Failed to load collections', { error: message });
    store.update((s) => ({
      ...s,
      isLoadingCollections: false,
      error: message,
    }));
  }
}

/**
 * Select a collection and load its items
 */
export async function selectCollection(collectionKey: string | null): Promise<void> {
  const current = get(store);
  if (current.selectedCollectionKey === collectionKey) return;

  log.info('Selecting collection', { collectionKey });

  store.update((s) => ({
    ...s,
    selectedCollectionKey: collectionKey,
    items: [],
    totalItems: 0,
  }));

  if (collectionKey && current.selectedLibrary) {
    await loadItems(current.selectedLibrary.id, current.selectedLibrary.type, collectionKey);
  }
}

/**
 * Load items for a collection
 */
export async function loadItems(
  libraryId: number,
  libraryType: LibraryType,
  collectionKey: string
): Promise<void> {
  // Cancel any pending request
  if (itemsAbortController) {
    itemsAbortController.abort();
  }
  itemsAbortController = new AbortController();

  log.debug('Loading items', { libraryId, libraryType, collectionKey });
  store.update((s) => ({ ...s, isLoadingItems: true, error: null }));

  try {
    const result = await zoteroService.getCollectionItems(
      libraryId,
      libraryType,
      collectionKey,
      { signal: itemsAbortController.signal }
    );

    if (!result.success) {
      if (result.error === 'Request aborted') return;
      throw new Error(result.error);
    }

    store.update((s) => ({
      ...s,
      items: result.data.items,
      totalItems: result.data.totalResults,
      isLoadingItems: false,
    }));

    log.info('Items loaded', { count: result.data.items.length, total: result.data.totalResults });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return;

    const message = error instanceof Error ? error.message : 'Failed to load items';
    log.error('Failed to load items', { error: message });
    store.update((s) => ({
      ...s,
      isLoadingItems: false,
      error: message,
    }));
  }
}

/**
 * Load all items from a library (not filtered by collection)
 */
export async function loadLibraryItems(
  libraryId: number,
  libraryType: LibraryType
): Promise<void> {
  // Cancel any pending request
  if (itemsAbortController) {
    itemsAbortController.abort();
  }
  itemsAbortController = new AbortController();

  log.debug('Loading library items', { libraryId, libraryType });
  store.update((s) => ({ ...s, isLoadingItems: true, error: null }));

  try {
    const result = await zoteroService.getLibraryItems(libraryId, libraryType, {
      signal: itemsAbortController.signal,
    });

    if (!result.success) {
      if (result.error === 'Request aborted') return;
      throw new Error(result.error);
    }

    store.update((s) => ({
      ...s,
      items: result.data.items,
      totalItems: result.data.totalResults,
      isLoadingItems: false,
    }));

    log.info('Library items loaded', {
      count: result.data.items.length,
      total: result.data.totalResults,
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return;

    const message = error instanceof Error ? error.message : 'Failed to load items';
    log.error('Failed to load library items', { error: message });
    store.update((s) => ({
      ...s,
      isLoadingItems: false,
      error: message,
    }));
  }
}

/**
 * Select a library and load its items
 */
export async function selectLibraryWithItems(library: StoredLibrary): Promise<void> {
  const current = get(store);
  const isSameLibrary =
    current.selectedLibrary?.id === library.id &&
    current.selectedLibrary?.type === library.type;

  log.info('Selecting library with items', { id: library.id, type: library.type });

  store.update((s) => ({
    ...s,
    selectedLibrary: library,
    selectedCollectionKey: null,
    items: [],
    totalItems: 0,
  }));

  // Load collections if different library
  if (!isSameLibrary) {
    await loadCollections(library.id, library.type);
  }

  // Load all items from library
  await loadLibraryItems(library.id, library.type);
}

/**
 * Load specific items by their keys (for citation resolution)
 * Returns the items but does NOT merge them into the store.
 * The store.items should only contain items from the currently selected collection/library.
 */
export async function loadItemsByKeys(itemKeys: string[]): Promise<ZoteroItem[]> {
  const current = get(store);

  // Need a library to fetch from
  if (!current.selectedLibrary && !current.userLibrary) {
    log.warn('Cannot load items by keys: no library available');
    return [];
  }

  const library = current.selectedLibrary || current.userLibrary!;

  log.info('Loading items by keys for citation resolution', {
    requested: itemKeys.length,
  });

  try {
    const result = await zoteroService.getItemsByKeys(library.id, library.type, itemKeys);

    if (!result.success) {
      const failedResult = result as { success: false; error: string };
      log.error('Failed to load items by keys', { error: failedResult.error });
      return [];
    }

    log.info('Citation items loaded', { count: result.data.length });
    return result.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load items';
    log.error('Failed to load items by keys', { error: message });
    return [];
  }
}

/**
 * Toggle collection expansion
 */
export function toggleExpanded(collectionKey: string): void {
  store.update((s) => {
    const newExpanded = new Set(s.expandedKeys);
    if (newExpanded.has(collectionKey)) {
      newExpanded.delete(collectionKey);
    } else {
      newExpanded.add(collectionKey);
    }
    return { ...s, expandedKeys: newExpanded };
  });
}

/**
 * Expand all ancestors of a collection
 */
export function expandAncestors(collectionKey: string): void {
  const state = get(store);
  const collection = state.collections.find((c) => c.key === collectionKey);
  if (!collection) return;

  const newExpanded = new Set(state.expandedKeys);
  let parentKey = collection.data.parentCollection;

  while (parentKey) {
    newExpanded.add(parentKey);
    const parent = state.collections.find((c) => c.key === parentKey);
    parentKey = parent?.data.parentCollection || false;
  }

  store.update((s) => ({ ...s, expandedKeys: newExpanded }));
}

/**
 * Clear error
 */
export function clearError(): void {
  store.update((s) => ({ ...s, error: null }));
}

/**
 * Reset store
 */
export function resetZoteroStore(): void {
  if (collectionsAbortController) {
    collectionsAbortController.abort();
  }
  if (itemsAbortController) {
    itemsAbortController.abort();
  }
  store.set({ ...initialState, expandedKeys: new Set() });
}

// =============================================================================
// EXPORTS
// =============================================================================

export const zoteroActions = {
  init: initZotero,
  selectLibrary,
  selectLibraryWithItems,
  selectCollection,
  loadCollections,
  loadItems,
  loadItemsByKeys,
  loadLibraryItems,
  toggleExpanded,
  expandAncestors,
  clearError,
  reset: resetZoteroStore,
};

export default zoteroStore;
